import type { TOC } from '@ember/component/template-only';
import { hash } from '@ember/helper';
import type { WithBoundArgs as WBA } from '@glint/template';
import type { EmberChangeset } from 'ember-changeset';
import ChangesetDatetimePicker from 'parog-web/components/changeset/datetime-picker';
import ChangesetTimePicker from 'parog-web/components/changeset/time-picker';
import ChatSendMessageForc from 'parog-web/components/chat/send-message-forc';
import ActionButton from 'ui-kit/components/action-button';
import CSAlignedFieldsRadioGroup from 'ui-kit/components/changeset/aligned-fields-radio-group';
import ChangesetBasicRadioGroup from 'ui-kit/components/changeset/basic-radio-group';
import ChangesetCheckbox from 'ui-kit/components/changeset/checkbox';
import type ConditionalRadioGroup from 'ui-kit/components/changeset/conditional-radio-group';
import ChangesetConditionalRadioGroup from 'ui-kit/components/changeset/conditional-radio-group';
import ChangesetDropdown from 'ui-kit/components/changeset/dropdown';
import ChangesetFieldsRadioGroup from 'ui-kit/components/changeset/fields-radio-group';
import ChangesetFileUpload from 'ui-kit/components/changeset/file-upload';
import type Input from 'ui-kit/components/changeset/labeled-input';
import ChangesetLabeledInput from 'ui-kit/components/changeset/labeled-input';
import ChangesetLabeledTextArea from 'ui-kit/components/changeset/labeled-text-area';
import ChangesetNumberInput from 'ui-kit/components/changeset/number-input';
import ChangesetPhone from 'ui-kit/components/changeset/phone';
import type Range from 'ui-kit/components/changeset/range';
import ChangesetRange from 'ui-kit/components/changeset/range';
import ValidatedField from 'ui-kit/components/validated-field';
import ChangesetDatePicker from '../../changeset/date-picker';

/** Common Bound Args */
type BA = 'changeset' | 'isReadOnly';

export interface Signature {
  Args: {
    changeset: EmberChangeset;
    isReadOnly?: boolean;
  };
  Blocks: {
    default: [
      {
        changeset: Signature['Args']['changeset'];
        ConditionalRadioGroup: WBA<typeof ConditionalRadioGroup, BA>;
        Range: WBA<typeof Range, 'changeset'>;
        Input: WBA<typeof Input, BA>;
        NumberInput: WBA<typeof ChangesetNumberInput, BA>;
        Date: WBA<typeof ChangesetDatePicker, BA>;
        FieldsRadioGroup: WBA<typeof ChangesetFieldsRadioGroup, BA>;
        AlignedFieldsRadioGroup: WBA<typeof CSAlignedFieldsRadioGroup, BA>;
        BasicRadioGroup: WBA<typeof ChangesetBasicRadioGroup, BA>;
        Dropdown: WBA<typeof ChangesetDropdown, BA>;
        TextArea: WBA<typeof ChangesetLabeledTextArea, BA>;
        Checkbox: WBA<typeof ChangesetCheckbox, BA>;
        FileUpload: WBA<typeof ChangesetFileUpload, 'changeset'>;
        ActionButton: WBA<typeof ActionButton, 'isReadOnly'>;
        SendMessage: WBA<typeof ChatSendMessageForc, 'changeset'>;
        Time: WBA<typeof ChangesetTimePicker, BA>;
        DateTime: WBA<typeof ChangesetDatetimePicker, BA>;
        Phone: WBA<typeof ChangesetPhone, BA>;
        ValidationFor: WBA<typeof ValidatedField, 'changeset' | 'errorClass'>;
      }
    ];
  };
}

const ForcProviderBase: TOC<Signature> = <template>
  {{yield
    (hash
      ValidationFor=(component
        ValidatedField
        changeset=@changeset
        errorClass='error-validated-input-display'
      )
      NumberInput=(component
        ChangesetNumberInput changeset=@changeset isReadOnly=@isReadOnly
      )
      FieldsRadioGroup=(component
        ChangesetFieldsRadioGroup changeset=@changeset isReadOnly=@isReadOnly
      )
      DateTime=(component
        ChangesetDatetimePicker changeset=@changeset isReadOnly=@isReadOnly
      )
      Time=(component
        ChangesetTimePicker changeset=@changeset isReadOnly=@isReadOnly
      )
      changeset=@changeset
      Input=(component
        ChangesetLabeledInput changeset=@changeset isReadOnly=@isReadOnly
      )
      Checkbox=(component
        ChangesetCheckbox changeset=@changeset isReadOnly=@isReadOnly
      )
      Dropdown=(component
        ChangesetDropdown changeset=@changeset isReadOnly=@isReadOnly
      )
      BasicRadioGroup=(component
        ChangesetBasicRadioGroup changeset=@changeset isReadOnly=@isReadOnly
      )
      AlignedFieldsRadioGroup=(component
        CSAlignedFieldsRadioGroup changeset=@changeset isReadOnly=@isReadOnly
      )
      ConditionalRadioGroup=(component
        ChangesetConditionalRadioGroup
        changeset=@changeset
        isReadOnly=@isReadOnly
      )
      Date=(component
        ChangesetDatePicker changeset=@changeset isReadOnly=@isReadOnly
      )
      Range=(component ChangesetRange changeset=@changeset)
      TextArea=(component
        ChangesetLabeledTextArea changeset=@changeset isReadOnly=@isReadOnly
      )
      SendMessage=(component ChatSendMessageForc changeset=@changeset)
      ActionButton=(component ActionButton isReadOnly=@isReadOnly)
      FileUpload=(component ChangesetFileUpload changeset=@changeset)
      Phone=(component
        ChangesetPhone changeset=@changeset isReadOnly=@isReadOnly
      )
    )
  }}
</template>;
export default ForcProviderBase;
